import { audioManager } from '../audio/audio-manager.js';
import { getAudioContext, resumeAudioContext, createPhoneCallEffect, createPivotVoiceEffect, getPivotVoiceParams } from '../audio/effects.js';

export type WordTimestamp = {
  word: string;
  start: number;
  end: number;
};

export interface TTSOptions {
  voice?: string;
  rate?: number;
  pitch?: number;
  useLemonFox?: boolean;
  useWebSpeech?: boolean;
  usePhoneEffect?: boolean;
}

const DEFAULT_LEMONFOX_VOICES = ['lily', 'heart', 'bella', 'kore', 'sarah', 'michael', 'echo', 'eric'];
const DEFAULT_VOICE = 'lily';
const DEFAULT_ENDPOINT = import.meta.env.VITE_LEMONFOX_TTS_URL ?? 'https://api.lemonfox.ai/v1/audio/speech';

// Global state for phone call effect (enabled by default)
let phoneCallEffectEnabled = true;
let phoneCallEffectIntensity = 0.8;

let speaking = false;
let lastText: string | null = null;
let currentWordTimestamps: WordTimestamp[] | null = null;
let currentAudioElement: HTMLAudioElement | null = null;
let currentObjectUrl: string | null = null;
let currentMediaSource: MediaElementAudioSourceNode | null = null;
let activeUtterance: SpeechSynthesisUtterance | null = null;

audioManager.onVolumeChange(volume => {
  if (currentAudioElement) {
    currentAudioElement.volume = volume;
  }
  if (activeUtterance) {
    activeUtterance.volume = volume;
  }
});

export async function speak(text: string, options: TTSOptions = {}): Promise<void> {
  stop();

  if (!text || !text.trim()) {
    return;
  }

  lastText = text;

  const preferLemonFox = options.useWebSpeech ? false : (options.useLemonFox ?? true);

  if (preferLemonFox) {
    try {
      await playThroughLemonFox(text, options);
      return;
    } catch (error) {
      // Fallback to Web Speech
    }
  }

  await speakWithWebSpeech(text, options);
}

export function stop(): void {
  // Clean up Web Audio nodes
  if (currentMediaSource) {
    try {
      currentMediaSource.disconnect();
    } catch (e) {
      // Ignore disconnect errors
    }
    currentMediaSource = null;
  }

  if (currentAudioElement) {
    currentAudioElement.pause();
    currentAudioElement.src = '';
    currentAudioElement = null;
  }

  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl);
    currentObjectUrl = null;
  }

  if (typeof window !== 'undefined' && window.speechSynthesis && speaking) {
    window.speechSynthesis.cancel();
  }

  activeUtterance = null;
  speaking = false;
}

export function isSpeaking(): boolean {
  return speaking;
}

export function getVoices(): SpeechSynthesisVoice[] {
  if (typeof window === 'undefined' || typeof window.speechSynthesis === 'undefined') {
  return [];
  }
  return window.speechSynthesis.getVoices();
}

export function getDefaultVoice(): SpeechSynthesisVoice | null {
  const voices = getVoices();
  return voices.length > 0 ? voices[0] : null;
}

export function getLemonFoxVoices(): string[] {
  return DEFAULT_LEMONFOX_VOICES;
}

export function getCurrentWordTimestamps(): WordTimestamp[] | null {
  return currentWordTimestamps;
}

export function getCurrentAudioElement(): HTMLAudioElement | null {
  return currentAudioElement;
}

export function setPhoneCallEffect(enabled: boolean, intensity: number = 0.8): void {
  phoneCallEffectEnabled = enabled;
  phoneCallEffectIntensity = Math.max(0, Math.min(1, intensity));
}

export function getPhoneCallEffectState(): { enabled: boolean; intensity: number } {
  return { enabled: phoneCallEffectEnabled, intensity: phoneCallEffectIntensity };
}

export function togglePhoneCallEffect(): boolean {
  phoneCallEffectEnabled = !phoneCallEffectEnabled;
  return phoneCallEffectEnabled;
}

async function playThroughLemonFox(text: string, options: TTSOptions): Promise<void> {
  const payload = await requestLemonFoxAudio(text, options.voice);
  const audio = new Audio(payload.audioUrl);
  audio.crossOrigin = "anonymous"; // Essential for Web Audio API
  audio.preload = 'auto';
  audio.volume = audioManager.getVolume();
  
  // Apply playback rate from Pivot Voice params
  const voiceParams = getPivotVoiceParams();
  if (voiceParams.playbackRate && voiceParams.playbackRate !== 1.0) {
    audio.playbackRate = voiceParams.playbackRate;
  }

  currentAudioElement = audio;
  currentObjectUrl = payload.ownsUrl ? payload.audioUrl : null;
  
  if (payload.wordTimestamps) {
    currentWordTimestamps = payload.wordTimestamps;
  } else {
    currentWordTimestamps = null;
    // Generate linear timestamps once duration is known
    audio.addEventListener('loadedmetadata', () => {
      if (currentAudioElement === audio && isFinite(audio.duration)) {
        currentWordTimestamps = generateLinearTimestamps(text, audio.duration);
      }
    }, { once: true });
  }

  speaking = true;

  // Apply phone effect if enabled (either globally or via options)
  // Explicit option overrides global state
  const usePhoneEffect = options.usePhoneEffect !== undefined 
    ? options.usePhoneEffect 
    : phoneCallEffectEnabled;

  if (usePhoneEffect) {
    try {
      await resumeAudioContext();
      const ctx = getAudioContext();
      
      // Create source node
      // Note: createMediaElementSource can only be called once per element
      const source = ctx.createMediaElementSource(audio);
      currentMediaSource = source;
      
      // Create effect chain and connect to destination
      // Replaced phone effect with Pivot Voice effect
      createPivotVoiceEffect(source, ctx.destination);
    } catch (error) {
      // Failed to apply voice effect, playing directly
    }
  }

  await audio.play();

  audio.addEventListener('ended', () => {
    speaking = false;
    
    // Clean up Web Audio nodes
    if (currentMediaSource) {
      try {
        currentMediaSource.disconnect();
      } catch (e) {}
      currentMediaSource = null;
    }

    if (currentAudioElement === audio) {
      currentAudioElement = null;
    }
    if (currentObjectUrl) {
      URL.revokeObjectURL(currentObjectUrl);
      currentObjectUrl = null;
    }
  }, { once: true });

  audio.addEventListener('error', (event) => {
    stop();
  }, { once: true });
}

async function requestLemonFoxAudio(text: string, voice?: string): Promise<{ audioUrl: string; wordTimestamps?: WordTimestamp[]; ownsUrl: boolean }> {
  const runtimeKey = typeof window !== 'undefined' ? (window as any).__LEMONFOX_API_KEY : undefined;
  const apiKey = import.meta.env.VITE_LEMONFOX_API_KEY || runtimeKey;

  // Log key presence (obscured)
  if (apiKey) {
    const obscured = apiKey.substring(0, 4) + '...' + apiKey.substring(apiKey.length - 4);
  } else {
  }

  if (!apiKey) {
    throw new Error('Missing VITE_LEMONFOX_API_KEY');
  }

  const response = await fetch(DEFAULT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      input: text,
      voice: voice || DEFAULT_VOICE,
      response_format: 'mp3',
    })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`LemonFox request failed: ${response.status} ${errorText}`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  return {
    audioUrl: url,
    wordTimestamps: undefined, // This endpoint does not support word timestamps
    ownsUrl: true
  };
}

function generateLinearTimestamps(text: string, duration: number): WordTimestamp[] {
  const words = text.trim().split(/\s+/);
  if (words.length === 0 || duration <= 0) return [];
  
  const durationPerWord = duration / words.length;
  return words.map((word, index) => ({
    word,
    start: index * durationPerWord,
    end: (index + 1) * durationPerWord
  }));
}

async function speakWithWebSpeech(text: string, options: TTSOptions): Promise<void> {
  if (typeof window === 'undefined' || typeof window.speechSynthesis === 'undefined') {
    throw new Error('Web Speech API not available');
  }

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.volume = audioManager.getVolume();
  if (options.rate) utterance.rate = options.rate;
  if (options.pitch) utterance.pitch = options.pitch;

  if (options.voice) {
    const matchingVoice = getVoices().find(voice => voice.name === options.voice);
    if (matchingVoice) {
      utterance.voice = matchingVoice;
    }
  }

  activeUtterance = utterance;
  currentAudioElement = null;
  currentWordTimestamps = null;
  speaking = true;

  await new Promise<void>((resolve, reject) => {
    utterance.onend = () => {
      speaking = false;
      activeUtterance = null;
      resolve();
    };
    utterance.onerror = (event) => {
      speaking = false;
      activeUtterance = null;
      reject(event.error);
    };
    window.speechSynthesis.speak(utterance);
  });
}
